import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Camera, 
  User, 
  LogOut, 
  X, 
  Mail, 
  Phone, 
  PlusCircle, 
  Briefcase, 
  MapPin, 
  Sparkles, 
  ChevronRight, 
  Award, 
  Info, 
  Users,
  Menu,
  Sun,
  Moon,
  Bell,
  Search,
  List,
  BookOpen,
  FileText,
  Bookmark,
  LayoutDashboard,
  Send
} from "lucide-react";
import apiCall from "../../../Redux/ApiCalls/AuthorizedApi";
import { 
  getMarketplaceProfile, 
  MarketplaceProfile, 
  getActivitySummary, 
  ActivitySummary,
  getPublicListings,
  getPublicProfiles,
  getPublicRequirements,
  MarketplaceListingListItem,
  MarketplaceRequirement
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { resolveImage } from "../../../Utils/helper";
import kwikMemoriesLogoIcon from "../../../assets/Icons/login/kwikmemories_logo.webp";
import kwikMemoriesWhiteLogoIcon from "../../../assets/images/marketplace/KM_whitelogo.webp";
import { isProfileCompleted } from "./MarketplaceShared";
import MarketplaceLoginModal from "./MarketplaceLoginPage";
import {
  HeaderWrap,
  HeaderContent,
  LogoArea,
  LogoImg,
  LogoText,
  LogoBadge,
  NavTabs,
  NavTab,
  SearchBarContainer,
  SearchBarInput,
  SearchBarButton,
  NotificationBtn,
  ThemeToggleSwitch,
  ThemeToggleKnob,
  ProfileCircleWrap,
  ActionsArea,
  ProfileBtn,
  CreateProfileBtn,
  LoginBtn,
  LogoutIconBtn,
  MobileMenuBtn,
  ModalOverlay,
  ModalCard,
  ModalTopArea,
  CloseBtn,
  ProfileBody,
  AvatarCircle,
  ProfileImg,
  ProfileName,
  ProfileTypeBadge,
  BioSection,
  BioText,
  DetailsList,
  DetailItem,
  DetailIconWrap,
  DetailContent,
  DetailLabel,
  DetailValue,
  TagsSection,
  TagsLabel,
  TagsContainer,
  TagChip,
  PortfolioSection,
  PortfolioLabel,
  PortfolioList,
  PortfolioLink,
  QuickActionsTitle,
  QuickActionsGrid,
  QuickActionCard,
  ActionIconWrap,
  ActionCardText,
  NoProfileCTA,
  CTAWarningText,
  ModalCreateProfileBtn,
  ModalLogoutBtn,
  ConfirmCard,
  ConfirmHeader,
  LogOutIconWrap,
  ConfirmActions,
  CancelConfirmBtn,
  ConfirmLogoutBtn,
  ConfirmBodyText,
  ConfirmTitle,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
  SearchResultsDropdown,
  SearchResultSectionTitle,
  SearchResultItem,
  SearchNoResults
} from "./MarketplaceHeaderStyles";
import { useMarketplaceTheme } from "./MarketplaceTheme";

interface UserInfo {
  name: string;
  phone: string;
  email?: string;
  country_code?: string;
}

const MarketplaceHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loggedIn, setLoggedIn] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showDropdown]);

  const [searchText, setSearchText] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [matchingEquipment, setMatchingEquipment] = useState<MarketplaceListingListItem[]>([]);
  const [matchingTalent, setMatchingTalent] = useState<MarketplaceProfile[]>([]);
  const [matchingRequirements, setMatchingRequirements] = useState<MarketplaceRequirement[]>([]);
  const [searching, setSearching] = useState(false);
  const { theme, toggleTheme } = useMarketplaceTheme();

  // Fetch results live on typing
  useEffect(() => {
    if (!searchText.trim()) {
      setMatchingEquipment([]);
      setMatchingTalent([]);
      setMatchingRequirements([]);
      setShowSearchResults(false);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setSearching(true);
      setShowSearchResults(true);
      try {
        const query = searchText.trim();
        const [listingsRes, profilesRes, reqsRes] = await Promise.all([
          getPublicListings({ page: 1, page_size: 4, search: query }),
          getPublicProfiles({ page: 1, page_size: 4, search: query }),
          getPublicRequirements({ page: 1, page_size: 4, search: query })
        ]);
        setMatchingEquipment(listingsRes.results || []);
        setMatchingTalent(profilesRes.results || []);
        setMatchingRequirements(reqsRes.results || []);
      } catch (err) {
        console.error("Live search failed", err);
      } finally {
        setSearching(false);
      }
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchText]);

  const isActiveTab = (tab: string) => {
    const params = new URLSearchParams(location.search);
    const currentTab = params.get("tab") || "";
    if (tab === "sell") {
      return location.pathname === "/marketplace/sell";
    }
    if (tab === "post-requirement") {
      return location.pathname === "/marketplace/post-requirement";
    }
    if (tab === "talent") {
      return location.pathname === "/marketplace/talent";
    }
    if (tab === "equipment") {
      return location.pathname === "/marketplace/equipment";
    }
    if (tab === "team") {
      return location.pathname === "/marketplace/team";
    }
    if (tab === "agency-directory") {
      return ["/marketplace/agency-directory", "/marketplace/agencies", "/marketplace/studios"].includes(location.pathname);
    }
    if (tab === "dashboard") {
      return location.pathname === "/marketplace/dashboard" || location.pathname === "/marketplace/home-dashboard";
    }
    if (location.pathname !== "/marketplace") return false;
    return currentTab === tab;
  };

  const [userInfo, setUserInfo] = useState<UserInfo | null>(() => {
    const cached = localStorage.getItem("user_info");
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        return {
          name: parsed.name || parsed.username || "",
          email: parsed.email || "",
          phone: parsed.phone_number || parsed.phone || "",
          country_code: parsed.country_code || ""
        };
      } catch {
        return null;
      }
    }
    return null;
  });
  const [profile, setProfile] = useState<MarketplaceProfile | null>(() => {
    const cached = localStorage.getItem("marketplace_profile");
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const data = parsed?.data || parsed || null;
        return isProfileCompleted(data) ? data : null;
      } catch {
        return null;
      }
    }
    return null;
  });
  const [profileChecked, setProfileChecked] = useState(!!localStorage.getItem("marketplace_profile"));
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [activitySummary, setActivitySummary] = useState<ActivitySummary | null>(null);

  const hasProfile = isProfileCompleted(profile);

  const handleTabClick = (tab: string) => {
    const hasToken = !!localStorage.getItem("access_token");
    if (["team", "sell", "post-requirement"].includes(tab)) {
      if (!hasToken) {
        showErrorToast("Please login first.");
        window.dispatchEvent(new CustomEvent("open-marketplace-login"));
        return;
      }
      if (profile && profile.is_active === false && (tab === "sell" || tab === "post-requirement")) {
        showErrorToast("Your marketplace account is frozen or deactivated by an administrator.");
        return;
      }
      if (!hasProfile) {
        showErrorToast("Create profile to access marketplace");
        navigate("/marketplace/profile/create");
        return;
      }
    }

    if (tab === "sell") {
      navigate("/marketplace/sell");
    } else if (tab === "post-requirement") {
      navigate("/marketplace/post-requirement");
    } else if (tab === "talent") {
      navigate("/marketplace/talent");
    } else if (tab === "equipment") {
      navigate("/marketplace/equipment");
    } else if (tab === "team") {
      navigate("/marketplace/team");
    } else if (tab === "agency-directory") {
      navigate("/marketplace/agency-directory");
    } else if (tab === "dashboard") {
      navigate("/marketplace/dashboard");
    } else if (tab === "") {
      navigate("/marketplace");
    } else {
      navigate(`/marketplace?tab=${tab}`);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchText.trim();
    setShowSearchResults(false);
    if (location.pathname === "/marketplace/talent") {
      navigate(query ? `/marketplace/talent?search=${encodeURIComponent(query)}` : `/marketplace/talent`);
    } else {
      navigate(query ? `/marketplace/equipment?search=${encodeURIComponent(query)}` : `/marketplace/equipment`);
    }
  };

  const handleClearSearch = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSearchText("");
    setShowSearchResults(false);
    setMatchingEquipment([]);
    setMatchingTalent([]);
    setMatchingRequirements([]);
    searchInputRef.current?.focus();
  };

  const refreshAuthState = useCallback(() => {
    let isMounted = true;
    const token = localStorage.getItem("access_token");
    const hasToken = !!token;
    setLoggedIn(hasToken);

    if (hasToken) {
      // 1. Immediately read cached profile from localStorage for instant UI response
      const cachedProfile = localStorage.getItem("marketplace_profile");
      if (cachedProfile) {
        try {
          const parsed = JSON.parse(cachedProfile);
          const prof = parsed?.data || parsed;
          if (prof && (prof.id || prof._id || prof.display_name || prof.is_public !== undefined)) {
            setProfile(prof);
            setProfileChecked(true);
          }
        } catch {
          // ignore
        }
      }

      // 1b. Immediately read cached user info
      const cachedUser = localStorage.getItem("user_info");
      if (cachedUser) {
        try {
          const parsed = JSON.parse(cachedUser);
          if (parsed) {
            setUserInfo({
              name: parsed.name || parsed.username || "",
              email: parsed.email || "",
              phone: parsed.phone_number || parsed.phone || "",
              country_code: parsed.country_code || ""
            });
          }
        } catch {
          // ignore
        }
      }

      // 2. Fetch fresh user details
      apiCall({ url: "/account/user-details/", method: "GET" })
        .then((res) => {
          if (!isMounted) return;
          const responseData = res?.data?.data || res?.data;
          if (responseData?.status && responseData?.user) {
            const userData = responseData.user;
            setUserInfo({
              name: userData.name || userData.username || "",
              email: userData.email || "",
              phone: userData.phone_number || userData.phone || "",
              country_code: userData.country_code || ""
            });
            localStorage.setItem("user_info", JSON.stringify(userData));
          }
        })
        .catch((e) => {
          if (!isMounted) return;
          const storedUser = localStorage.getItem("user_info");
          if (storedUser) {
            try { setUserInfo(JSON.parse(storedUser)); } catch { }
          }
        });

      // 3. Fetch fresh marketplace profile
      getMarketplaceProfile()
        .then((res) => {
          if (!isMounted) return;
          const profileData = res?.data || null;
          if (profileData && isProfileCompleted(profileData)) {
            setProfile(profileData);
            localStorage.setItem("marketplace_profile", JSON.stringify(profileData));
          } else {
            setProfile(null);
            localStorage.removeItem("marketplace_profile");
          }
          setProfileChecked(true);
        })
        .catch(() => {
          if (!isMounted) return;
          const stillCached = localStorage.getItem("marketplace_profile");
          if (stillCached) {
            try {
              const parsed = JSON.parse(stillCached);
              const data = parsed?.data || parsed;
              if (isProfileCompleted(data)) {
                setProfile(data);
              } else {
                setProfile(null);
                localStorage.removeItem("marketplace_profile");
              }
            } catch {
              setProfile(null);
              localStorage.removeItem("marketplace_profile");
            }
          } else {
            setProfile(null);
          }
          setProfileChecked(true);
        });

      // 4. Fetch activity summary
      getActivitySummary()
        .then((res) => {
          if (isMounted && res?.data) setActivitySummary(res.data);
        })
        .catch(() => {});

      return () => {
        isMounted = false;
      };
    } else {
      setUserInfo(null);
      setProfile(null);
      setProfileChecked(false);
      setActivitySummary(null);
    }
  }, []);

  useEffect(() => {
    refreshAuthState();
    window.addEventListener("marketplace-auth-change", refreshAuthState);
    window.addEventListener("storage", refreshAuthState);

    return () => {
      window.removeEventListener("marketplace-auth-change", refreshAuthState);
      window.removeEventListener("storage", refreshAuthState);
    };
  }, [refreshAuthState]);

  useEffect(() => {
    refreshAuthState();
  }, [location.pathname, refreshAuthState]);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user_info");
    localStorage.removeItem("marketplace_profile");
    localStorage.removeItem("marketplace_profile_created");
    localStorage.removeItem("marketplace_profile_skipped");
    setLoggedIn(false);
    setProfile(null);
    setUserInfo(null);
    setShowProfile(false);
    setShowLogoutConfirm(false);
    window.dispatchEvent(new CustomEvent("marketplace-auth-change"));
    navigate("/marketplace");
  };

  return (
    <>
      <HeaderWrap $theme={theme}>
        <HeaderContent>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0, flexShrink: 0 }}>
            <MobileMenuBtn 
              $theme={theme} 
              onClick={() => window.dispatchEvent(new CustomEvent("toggle-marketplace-sidebar"))}
              title="Open Navigation Menu"
              type="button"
            >
              <Menu size={20} />
            </MobileMenuBtn>
            <LogoArea onClick={() => navigate("/marketplace")}>
              <LogoImg src={theme === "dark" ? kwikMemoriesWhiteLogoIcon : kwikMemoriesLogoIcon} alt="KwikMemories Logo" />
              <LogoText $theme={theme}>KwikMemories</LogoText>
              <LogoBadge>Marketplace</LogoBadge>
            </LogoArea>
          </div>

          <NavTabs>
            <NavTab $theme={theme} $active={isActiveTab("")} onClick={() => handleTabClick("")}>Home</NavTab>
            {/* <NavTab $theme={theme} $active={isActiveTab("dashboard")} onClick={() => handleTabClick("dashboard")}>Dashboard</NavTab> */}
            <NavTab $theme={theme} $active={isActiveTab("equipment")} onClick={() => handleTabClick("equipment")}>Equipment</NavTab>
            <NavTab $theme={theme} $active={isActiveTab("talent")} onClick={() => handleTabClick("talent")}>Talent</NavTab>
            {/* <NavTab $theme={theme} $active={isActiveTab("agency-directory")} onClick={() => handleTabClick("agency-directory")}>Agency Directory</NavTab> */}
            <NavTab $theme={theme} $active={isActiveTab("team")} onClick={() => handleTabClick("team")}>Teams</NavTab>
            <NavTab $theme={theme} $active={isActiveTab("sell")} onClick={() => handleTabClick("sell")}>Sell/Rent</NavTab>
            <NavTab $theme={theme} $active={isActiveTab("post-requirement")} onClick={() => handleTabClick("post-requirement")}>Post Job</NavTab>
          </NavTabs>

          <ActionsArea>
            <SearchBarContainer onSubmit={handleSearchSubmit} $theme={theme}>
              <SearchBarInput 
                ref={searchInputRef}
                type="text" 
                placeholder="Search gear or talent..." 
                value={searchText} 
                onChange={(e) => setSearchText(e.target.value)}
                onFocus={() => searchText.trim() && setShowSearchResults(true)}
                $theme={theme}
              />
              {searchText.length > 0 ? (
                <SearchBarButton 
                  type="button" 
                  $theme={theme} 
                  onClick={handleClearSearch}
                  title="Clear search"
                  aria-label="Clear search"
                >
                  <X size={15} />
                </SearchBarButton>
              ) : (
                <SearchBarButton type="submit" $theme={theme} title="Search" aria-label="Search">
                  <Search size={15} />
                </SearchBarButton>
              )}

              {showSearchResults && (
                <>
                  <div style={{ position: "fixed", inset: 0, zIndex: 9999 }} onClick={() => setShowSearchResults(false)} />
                  <SearchResultsDropdown $theme={theme}>
                    {searching ? (
                      <SearchNoResults>Searching...</SearchNoResults>
                    ) : (
                      <>
                        {matchingEquipment.length === 0 && matchingTalent.length === 0 && matchingRequirements.length === 0 ? (
                          <SearchNoResults>No matches found</SearchNoResults>
                        ) : (
                          <>
                            {matchingEquipment.length > 0 && (
                              <>
                                <SearchResultSectionTitle>Equipment</SearchResultSectionTitle>
                                {matchingEquipment.map(item => (
                                  <SearchResultItem 
                                    key={item.id} 
                                    onClick={() => { 
                                      setShowSearchResults(false);
                                      setSearchText("");
                                      navigate(`/marketplace/listing/${item.id}`); 
                                    }}
                                  >
                                    <div className="title">{item.title}</div>
                                    <div className="subtitle">₹{item.price} • {item.listing_type}</div>
                                  </SearchResultItem>
                                ))}
                              </>
                            )}

                            {matchingTalent.length > 0 && (
                              <>
                                <SearchResultSectionTitle>Talent Profiles</SearchResultSectionTitle>
                                {matchingTalent.map(profile => (
                                  <SearchResultItem 
                                    key={profile.id} 
                                    onClick={() => { 
                                      setShowSearchResults(false);
                                      setSearchText("");
                                      navigate(`/marketplace/profile/${profile.id}`); 
                                    }}
                                  >
                                    <div className="title">{profile.display_name}</div>
                                    <div className="subtitle">{profile.profession || profile.profile_type}</div>
                                  </SearchResultItem>
                                ))}
                              </>
                            )}

                            {matchingRequirements.length > 0 && (
                              <>
                                <SearchResultSectionTitle>Requirements / Jobs</SearchResultSectionTitle>
                                {matchingRequirements.map(req => (
                                  <SearchResultItem 
                                    key={req.id} 
                                    onClick={() => { 
                                      setShowSearchResults(false);
                                      setSearchText("");
                                      navigate(`/marketplace/equipment`); 
                                    }}
                                  >
                                    <div className="title">{req.title}</div>
                                    <div className="subtitle">{req.requirement_type === "equipment_requirement" ? "Gear Wanted" : "Gig / Job"}</div>
                                  </SearchResultItem>
                                ))}
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </SearchResultsDropdown>
                </>
              )}
            </SearchBarContainer>

            <ThemeToggleSwitch 
              onClick={toggleTheme} 
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`} 
              $isDark={theme === "dark"}
              type="button"
            >
              <Sun size={12} color={theme === "dark" ? "#FFC951" : "rgba(216,99,186,0.5)"} style={{ opacity: theme === "dark" ? 1 : 0.4 }} />
              <Moon size={12} color={theme === "dark" ? "rgba(255,255,255,0.4)" : "#D863BA"} style={{ opacity: theme === "dark" ? 0.4 : 1 }} />
              <ThemeToggleKnob $isDark={theme === "dark"}>
                {theme === "dark" ? <Moon size={11} color="#ffffff" /> : <Sun size={11} color="#ffffff" />}
              </ThemeToggleKnob>
            </ThemeToggleSwitch>

            {/* <NotificationBtn title="Notifications" $theme={theme}>
              <Bell size={16} />
            </NotificationBtn> */}

            {loggedIn ? (
              <div style={{ position: "relative", flexShrink: 0, display: "flex", alignItems: "center" }}>
                <ProfileCircleWrap $theme={theme} onClick={() => setShowDropdown(!showDropdown)} title="Profile Options">
                  {profile?.profile_picture ? (
                    <img src={resolveImage(profile.profile_picture)} alt={profile.display_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    profile?.display_name?.charAt(0).toUpperCase() || userInfo?.name?.charAt(0).toUpperCase() || <User size={15} />
                  )}
                </ProfileCircleWrap>
                              {showDropdown && (
                  <>
                    <DropdownMenu $theme={theme} ref={dropdownRef}>
                      {hasProfile ? (
                        <>
                          {/* <DropdownItem onClick={() => { setShowDropdown(false); navigate("/marketplace/dashboard"); }}>
                            <LayoutDashboard size={14} color="#c94daa" /> Dashboard
                          </DropdownItem> */}

                          <DropdownItem onClick={() => { setShowDropdown(false); navigate("/marketplace/profile"); }}>
                            <User size={14} color="#c94daa" /> View Profile
                          </DropdownItem>

                          <DropdownItem onClick={() => { setShowDropdown(false); navigate("/marketplace/my-listings"); }}>
                            <List size={14} color="#c94daa" /> My Listings
                          </DropdownItem>

                          <DropdownItem onClick={() => { setShowDropdown(false); navigate("/marketplace/my-bookings"); }}>
                            <BookOpen size={14} color="#c94daa" /> My Bookings
                          </DropdownItem>

                          <DropdownItem onClick={() => { setShowDropdown(false); navigate("/marketplace/received-applications"); }}>
                            <FileText size={14} color="#c94daa" /> Received Applications
                          </DropdownItem>

                          <DropdownItem onClick={() => { setShowDropdown(false); navigate("/marketplace/my-orders"); }}>
                            <Send size={14} color="#c94daa" /> Custom Quotes
                          </DropdownItem>

                          {/* Saved Items — always visible when logged in */}
                          <DropdownItem onClick={() => { setShowDropdown(false); navigate("/marketplace/saved"); }} id="header-saved-items">
                            <Bookmark size={14} color="#c94daa" /> Saved Items
                          </DropdownItem>
                          <DropdownDivider $desktopOnly={true} />
                        </>
                      ) : (
                        <DropdownItem onClick={() => { setShowDropdown(false); navigate("/marketplace/profile/create"); }}>
                          <PlusCircle size={14} color="#c94daa" /> Create Profile
                        </DropdownItem>
                      )}
                      <DropdownItem $desktopOnly={true} onClick={() => { setShowDropdown(false); setShowLogoutConfirm(true); }}>
                        <LogOut size={14} color="#ef4444" /> Sign Out
                      </DropdownItem>
                    </DropdownMenu>
                  </>
                )}
              </div>
            ) : (
              <LoginBtn onClick={() => window.dispatchEvent(new CustomEvent("open-marketplace-login"))}>
                <User size={15} />
                <span>Login</span>
              </LoginBtn>
            )}
          </ActionsArea>
        </HeaderContent>
      </HeaderWrap>

      {showProfile && (
        <ModalOverlay onClick={() => setShowProfile(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTopArea>
              <CloseBtn onClick={() => setShowProfile(false)}>
                <X size={18} color="white" />
              </CloseBtn>
              {profile?.profile_picture ? (
                <ProfileImg src={resolveImage(profile.profile_picture)} alt={profile.display_name} />
              ) : (
                <AvatarCircle>
                  {profile?.display_name?.charAt(0).toUpperCase() || userInfo?.name?.charAt(0).toUpperCase() || <User size={24} />}
                </AvatarCircle>
              )}
              <ProfileName>{profile?.display_name || userInfo?.name || "N/A"}</ProfileName>
              {profile && (
                <ProfileTypeBadge>
                  {profile.profile_type === "studio" ? "Studio" : "Freelancer"}
                </ProfileTypeBadge>
              )}
            </ModalTopArea>

            <ProfileBody>
              {profile?.bio && (
                <BioSection>
                  <BioText>"{profile.bio}"</BioText>
                </BioSection>
              )}

              <DetailsList>
                {(profile?.city || profile?.state || profile?.country) && (
                  <DetailItem>
                    <DetailIconWrap><MapPin size={16} color="#c94daa" /></DetailIconWrap>
                    <DetailContent>
                      <DetailLabel>Location</DetailLabel>
                      <DetailValue>
                        {[profile.city, profile.state, profile.country].filter(Boolean).join(", ")}
                      </DetailValue>
                    </DetailContent>
                  </DetailItem>
                )}

                <DetailItem>
                  <DetailIconWrap><Mail size={16} color="#c94daa" /></DetailIconWrap>
                  <DetailContent>
                    <DetailLabel>Email Address</DetailLabel>
                    <DetailValue>{profile?.contact_email || userInfo?.email || "Not Provided"}</DetailValue>
                  </DetailContent>
                </DetailItem>

                <DetailItem>
                  <DetailIconWrap><Phone size={16} color="#c94daa" /></DetailIconWrap>
                  <DetailContent>
                    <DetailLabel>Phone Number</DetailLabel>
                    <DetailValue>
                      {profile?.contact_phone || (userInfo?.phone ? `${userInfo.country_code ? `${userInfo.country_code} ` : ""}${userInfo.phone}` : "N/A")}
                    </DetailValue>
                  </DetailContent>
                </DetailItem>

                {profile?.profile_type === "freelancer" && (
                  <>
                    {profile.profession && (
                      <DetailItem>
                        <DetailIconWrap><Briefcase size={16} color="#c94daa" /></DetailIconWrap>
                        <DetailContent>
                          <DetailLabel>Profession</DetailLabel>
                          <DetailValue>{profile.profession}</DetailValue>
                        </DetailContent>
                      </DetailItem>
                    )}
                    {profile.experience_years !== undefined && profile.experience_years !== null && (
                      <DetailItem>
                        <DetailIconWrap><Award size={16} color="#c94daa" /></DetailIconWrap>
                        <DetailContent>
                          <DetailLabel>Experience</DetailLabel>
                          <DetailValue>{profile.experience_years} Years</DetailValue>
                        </DetailContent>
                      </DetailItem>
                    )}
                  </>
                )}

                {profile?.profile_type === "studio" && (
                  <>
                    {profile.team_size !== undefined && profile.team_size !== null && (
                      <DetailItem>
                        <DetailIconWrap><Users size={16} color="#c94daa" /></DetailIconWrap>
                        <DetailContent>
                          <DetailLabel>Team Size</DetailLabel>
                          <DetailValue>{profile.team_size} members</DetailValue>
                        </DetailContent>
                      </DetailItem>
                    )}
                    {profile.business_info && (
                      <DetailItem>
                        <DetailIconWrap><Info size={16} color="#c94daa" /></DetailIconWrap>
                        <DetailContent>
                          <DetailLabel>Business Info</DetailLabel>
                          <DetailValue>{profile.business_info}</DetailValue>
                        </DetailContent>
                      </DetailItem>
                    )}
                  </>
                )}
              </DetailsList>

              {profile?.profile_type === "freelancer" && profile?.skills && profile.skills.length > 0 && (
                <TagsSection>
                  <TagsLabel>Skills</TagsLabel>
                  <TagsContainer>
                    {profile.skills.map(skill => (
                      <TagChip key={skill}>{skill}</TagChip>
                    ))}
                  </TagsContainer>
                </TagsSection>
              )}

              {profile?.profile_type === "studio" && profile?.services_offered && profile.services_offered.length > 0 && (
                <TagsSection>
                  <TagsLabel>Services Offered</TagsLabel>
                  <TagsContainer>
                    {profile.services_offered.map(service => (
                      <TagChip key={service}>{service}</TagChip>
                    ))}
                  </TagsContainer>
                </TagsSection>
              )}

              {profile?.portfolio && profile.portfolio.length > 0 && (
                <PortfolioSection>
                  <PortfolioLabel>Portfolio & Work Links</PortfolioLabel>
                  <PortfolioList>
                    {profile.portfolio.map((item, idx) => (
                      <PortfolioLink key={idx} href={item.url} target="_blank" rel="noopener noreferrer">
                        <span>{item.title}</span>
                        <ChevronRight size={14} />
                      </PortfolioLink>
                    ))}
                  </PortfolioList>
                </PortfolioSection>
              )}

              {hasProfile ? (
                <>
                  <QuickActionsTitle>Quick Actions</QuickActionsTitle>
                  <QuickActionsGrid>
                    <QuickActionCard
                      onClick={() => {
                        setShowProfile(false);
                        if (profile && profile.is_active === false) {
                          showErrorToast("Your marketplace account is frozen by an administrator.");
                          return;
                        }
                        navigate("/marketplace/sell");
                      }}
                    >
                      <ActionIconWrap><Camera size={16} /></ActionIconWrap>
                      <ActionCardText>
                        <strong>Sell / Rent Equipment</strong>
                        <span>Put cameras, lenses on hire/sale</span>
                      </ActionCardText>
                    </QuickActionCard>

                    <QuickActionCard
                      onClick={() => {
                        setShowProfile(false);
                        if (profile && profile.is_active === false) {
                          showErrorToast("Your marketplace account is frozen by an administrator.");
                          return;
                        }
                        navigate("/marketplace/post-requirement");
                      }}
                    >
                      <ActionIconWrap><PlusCircle size={16} /></ActionIconWrap>
                      <ActionCardText>
                        <strong>Post a Requirement</strong>
                        <span>Post what equipment or service you need</span>
                      </ActionCardText>
                    </QuickActionCard>

                    <QuickActionCard onClick={() => { setShowProfile(false); navigate("/marketplace/my-listings"); }}>
                      <ActionIconWrap><Sparkles size={16} /></ActionIconWrap>
                      <ActionCardText>
                        <strong>My Listings</strong>
                        <span>Manage your active listings</span>
                      </ActionCardText>
                    </QuickActionCard>

                    <QuickActionCard onClick={() => { setShowProfile(false); navigate("/marketplace/my-bookings"); }}>
                      <ActionIconWrap><Briefcase size={16} /></ActionIconWrap>
                      <ActionCardText>
                        <strong>My Bookings</strong>
                        <span>View received inquiries & bookings</span>
                      </ActionCardText>
                    </QuickActionCard>
                  </QuickActionsGrid>
                </>
              ) : (
                <NoProfileCTA>
                  <CTAWarningText>You don't have a professional seller profile yet.</CTAWarningText>
                  <ModalCreateProfileBtn onClick={() => { setShowProfile(false); navigate("/marketplace/profile/create"); }}>
                    Create Marketplace Profile
                  </ModalCreateProfileBtn>
                </NoProfileCTA>
              )}

               <ModalLogoutBtn onClick={() => { setShowProfile(false); setShowLogoutConfirm(true); }}>
                <LogOut size={16} /> Sign Out
              </ModalLogoutBtn>
            </ProfileBody>
          </ModalCard>
        </ModalOverlay>
      )}

      {showLogoutConfirm && (
        <ModalOverlay onClick={() => setShowLogoutConfirm(false)}>
          <ConfirmCard onClick={(e) => e.stopPropagation()}>
            <ConfirmHeader>
              <LogOutIconWrap>
                <LogOut size={22} color="#c94daa" />
              </LogOutIconWrap>
              <ConfirmTitle>Sign Out</ConfirmTitle>
            </ConfirmHeader>
            <ConfirmBodyText>
              Are you sure you want to sign out from the marketplace?
            </ConfirmBodyText>
            <ConfirmActions>
              <CancelConfirmBtn onClick={() => setShowLogoutConfirm(false)}>
                Cancel
              </CancelConfirmBtn>
              <ConfirmLogoutBtn onClick={handleLogout}>
                Sign Out
              </ConfirmLogoutBtn>
            </ConfirmActions>
          </ConfirmCard>
        </ModalOverlay>
      )}
      <MarketplaceLoginModal />
    </>
  );
};

export default MarketplaceHeader;
